import classNames from 'classnames'
import { useUserCountry } from 'hooks/useUserCountry'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { PropsWithClassName } from 'types'

export type SizeShape = 'Square' | 'Disc' | 'Statue'

export type Size = {
  shape?: SizeShape
  set?: boolean
  width?: number
  height?: number
  depth?: number
  diameter?: number
  metric?: boolean
  sizeType?: string
}

type Props = {
  size: Size
} & PropsWithClassName

export const SizeDisplay = ({
  size: { metric, shape = 'Square', width, height, depth, diameter, set },
  className,
}: Props) => {
  const { t } = useTranslation()
  const { userCountry } = useUserCountry()
  const [artworkMeasurement, setArtworkMeasurement] = useState<
    'cm' | 'inch' | undefined
  >(undefined)

  useEffect(() => {
    setArtworkMeasurement(
      userCountry === 'US' ? 'inch' : metric ? 'cm' : 'inch'
    )
  }, [userCountry, metric])

  const ToggleMeasurement = () => (
    <div className='flex items-center'>
      <button
        type='button'
        onClick={() => setArtworkMeasurement('cm')}
        className={`cursor-pointer border border-solid border-cool-400 px-2 py-0.5 text-sm text-cool-900 ${
          artworkMeasurement === 'cm' && 'border-cool-900'
        }`}
      >
        cm
      </button>
      <button
        type='button'
        onClick={() => setArtworkMeasurement('inch')}
        className={`cursor-pointer border border-solid border-cool-400 px-2 py-0.5 text-sm text-cool-900 ${
          artworkMeasurement === 'inch' && 'border-cool-900'
        }`}
      >
        inch
      </button>
    </div>
  )

  if (shape === 'Square' || shape === 'Statue') {
    if (!width && !height && !depth) return null

    return (
      <div className={classNames('flex items-center space-x-4', className)}>
        {artworkMeasurement === 'cm' && (
          <p className='flex flex-wrap items-center'>
            <span className={`${set && 'mr-1'}`}>
              {width ? (
                <>
                  {!height && 'W '}
                  {width}
                </>
              ) : null}
              {height ? (
                <>
                  {width ? ' x ' : 'H '}
                  {height}
                </>
              ) : null}{' '}
              {depth ? (
                <>
                  {width || height ? ' x ' : 'D '}
                  {depth}
                </>
              ) : null}{' '}
              cm
            </span>
            {set && <span>{t('single_artwork:each')}</span>}
          </p>
        )}

        {artworkMeasurement === 'inch' && (
          <p>
            {width ? (
              <>
                {!height && 'W '}

                {parseInt((width / 2.54).toString()) ===
                Number((width / 2.54).toFixed(1))
                  ? parseInt((width / 2.54).toString())
                  : Number((width / 2.54).toFixed(1))}
              </>
            ) : null}
            {height ? (
              <>
                {width ? ' x ' : 'H '}
                {parseInt((height / 2.54).toString()) ===
                Number((height / 2.54).toFixed(1))
                  ? parseInt((height / 2.54).toString())
                  : Number((height / 2.54).toFixed(1))}
              </>
            ) : null}{' '}
            {depth ? (
              <>
                {width || height ? ' x ' : 'D '}
                {parseInt((depth / 2.54).toString()) ===
                Number((depth / 2.54).toFixed(1))
                  ? parseInt((depth / 2.54).toString())
                  : Number((depth / 2.54).toFixed(1))}
              </>
            ) : null}{' '}
            inch
          </p>
        )}

        <ToggleMeasurement />
      </div>
    )
  }

  if (shape === 'Disc') {
    if (!diameter) return null
    return (
      <div className='flex items-center space-x-4'>
        {artworkMeasurement === 'cm' && <p>⌀ {diameter} cm</p>}
        {artworkMeasurement === 'inch' && (
          <p>⌀ {(diameter / 2.54).toFixed(2)} inch</p>
        )}
        <ToggleMeasurement />
      </div>
    )
  }

  return null
}
